import React from 'react';
import styled from 'styled-components';
import { useSermonSeries } from '../hooks/useSermonSeries';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { Permission, hasAnyPermission } from '../utils/roles';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
`;

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  gap: 1rem;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const NewButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary);
  color: #fff;
  padding: 0.55rem 0.9rem;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);
`;

export const SermonSeriesPage: React.FC = () => {
  const { data, isLoading, error } = useSermonSeries();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageContent = hasAnyPermission(roles, [Permission.MANAGE_CONTENT]);

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p>Error loading series</p>;

  return (
    <Page>
      <HeaderRow>
        <h1 style={{ margin: 0 }}>Sermon Series</h1>
        {canManageContent ? <NewButton to="/sermon-series/new">+ New Series</NewButton> : null}
      </HeaderRow>
      <Wrapper>
        {data?.map((s) => (
          <Card key={s.id} onClick={() => navigate(`/sermon-series/${s.id}`)} style={{ cursor: 'pointer' }}>
            {s.image_url && (
              <img
                src={s.image_url}
                alt={s.title}
                style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 12, marginBottom: 8 }}
              />
            )}
            <h3 style={{ margin: '0 0 0.25rem 0' }}>{s.title}</h3>
            {s.subtitle && <p style={{ margin: 0, color: '#6b7280' }}>{s.subtitle}</p>}
            <p style={{ margin: '0.35rem 0 0 0', color: '#6b7280', fontSize: 12 }}>
              {s.sermon_count} sermons
            </p>
          </Card>
        ))}
      </Wrapper>
    </Page>
  );
};

export default SermonSeriesPage;
